import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PageHeader} from "../components/layout/PageHeader";
import {showError} from "../components/Notification";
import {Button, Col, Form, Input, Row, Select, Spin, Upload, DatePicker} from "antd";
import {getTemplate, getTemplateGuid, templateGuidSubject, useTemplate, useTemplateGuid} from "../hooks/template";
import {
    CertificateDto,
    CertificateListItemDto,
    CertificateParameterValueDto,
    TemplateListItemDto,
    TemplateParameterDto
} from "../generated-api";
import {createCertificate, useCertificateCreate, deleteDocumentGuid} from "../hooks/certificate";
import {useHistory,useLocation} from "react-router-dom";
import {ResponseSubjectState} from "../hooks";
import {FilePlus, Link, Maximize, Search, Upload as UploadIcon, XCircle} from "react-feather";
import {SearchCertificates} from "../components/SearchCertificates";
import {ScanQrCode} from "../components/ScanQRCode";
import {getCustomHeaders} from "../hooks/user";
import {CertificateAnimation} from "../components/CertificateAnimation";
import {useForm} from "antd/es/form/Form";
import {UPLOAD_FILE_SIZE} from "../constants";
import {BackButton, IConfirmProps} from "../components/layout/BackButton";
import {getBusinessSectors, useBusinessSectors} from "../hooks/businessSectors";
import {TEMPLATE_PARAM_DATE} from "../constants/template";
import {dateFormat} from "../utils/render";
import { CertificatesListSearch } from "../components/CertificatesListSearch";

const {REACT_APP_API_DOMAIN} = process.env;

export function CertificateAddPage() {
    const {t} = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const templateResponse = useTemplate();
    const templates: TemplateListItemDto[] = templateResponse.data || [];
    const queryParams = new URLSearchParams(location.search); 
    const businessSectorsResponse = useBusinessSectors();
    const businessSectors = (businessSectorsResponse || {}).data || [];
    const certificateCreateResponse = useCertificateCreate();
    const [templateId, setTemplateId] = useState('');
    const [businessSectorId, setBusinessSectorId] = useState('');
    const templateGuidResponse = useTemplateGuid();
    const templateParameters: TemplateParameterDto[] = (templateGuidResponse.data || {}).templateParameters || [];

    const [links, setLinks] = useState<CertificateListItemDto[]>([]);
    const [searchOpen, setSearchOpen] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [savingAnimation, setSavingAnimation] = useState(false);
    const [responseId, setResponseId] = useState();
    const [fileList, setFileList] = useState<any[]>([]);
    const disableAnimation = localStorage.getItem('disableAnimation');
    const [form] = useForm();

    useEffect(() => {
        templateGuidSubject.next(new ResponseSubjectState());
        getBusinessSectors().then( () => setBusinessSectorId(queryParams.get('businessSector') || '')).catch(console.error);
        getTemplate(true).then( () => changeTemplate(queryParams.get('template') || '')).catch(console.error);
       
        if (isAddSameCertificate()){
            form.setFieldsValue({
                businessSector: queryParams.get('businessSector') || '',
                template: queryParams.get('template') || ''
              }); 
        }
    }, [true]);

    const isAddSameCertificate = (): boolean => {
        return queryParams.get('businessSector') != null && queryParams.get('template') != null;
    }

    const onFinish = (values: any): void => {
        !disableAnimation && setSavingAnimation(true);
        const certificateParameterValues: CertificateParameterValueDto[] = [];
        const prefix = 'certificateParameterValues.';
        Object.keys(values).forEach(key => {
            if (key.indexOf(prefix) === 0) {
                const templateParameterId = key.split(prefix)[1];
                certificateParameterValues.push({
                    templateParameterId,
                    value: values[key] || ""
                } as any);
            }
        });

        createCertificate(templateId, {
            certificateParameterValues,
            parentCertificates: links.map(link => link.id as string),
            certificateFiles: fileList.map(file => file.response)
        }).then(response => {
            if (disableAnimation) {
                history.push(`/certificates/${response.id}?businessSector=${businessSectorId}`);
            } else {
                setResponseId(response.id);
            }
        }).catch(error => {
            console.error(error);
            !disableAnimation && setSavingAnimation(false);
        });
    };

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        }
    };

    const changeTemplate = (templateId: string): void => {
        setTemplateId(templateId);
        if (templateId) {
            getTemplateGuid(templateId).then().catch(console.error);
        } else {
            templateGuidSubject.next(new ResponseSubjectState());
        }
    };

    const changeBusinessSector = (businessSectorId: string): void => {
        setBusinessSectorId(businessSectorId);
        changeTemplate('');
        form.setFieldsValue({...form.getFieldsValue(), template: null});
    };

    const beforeUpload = (file: any): boolean => {
        if (file.size > (UPLOAD_FILE_SIZE * 1024 * 1024)) {
            showError(t('general.error'), t('error.fileUploadSize').replace('${max}', String(UPLOAD_FILE_SIZE)));
            return false;
        }
        return true;
    };

    const handleRemoveFile = (file: any) => {
        deleteDocumentGuid(file.response.fileId)
            .then(() => setFileList(fileList.filter(item => item.uid !== file.uid)))
            .catch((error) => console.error(error));
       
    };

    const deleteAllPendingFiles = async() => {
        return Promise.all(fileList.map(async(file) => await deleteDocumentGuid(file.response.fileId)));
    }

    const confirmProps: IConfirmProps = { onOk: () => deleteAllPendingFiles(), modalHeader: t('certificate.backConfirm.header'), modalBody: t('certificate.backConfirm.body')};

    const filteredTemplates = templates.filter(item => item.businessSectorId === businessSectorId);

    const loading = templateResponse.loading || templateGuidResponse.loading || (disableAnimation && certificateCreateResponse.loading);

    return (
        <Spin spinning={loading}>
            <PageHeader/>
            <Form name="certificate"
                  form={form}
                  onFinish={onFinish}
                  autoComplete={'off'}
                  labelCol={{span: 11}}
                  validateMessages={validateMessages}
                  validateTrigger={'onSubmit'}>
                <div className={'top-block accent2-background'}>
                    <h1 className={'h1'}>
                        <BackButton backPath={'/'} confirmProps={ confirmProps } /><FilePlus/>{t('certificate.addTitle')}
                    </h1>
                    <Row className={'row-offset'}>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('certificate.businessSector')} name={'businessSector'}
                                       rules={[{required: true}]}>
                                <Select allowClear showSearch style={{width: '100%'}}
                                        placeholder={t('general.notSelected')}
                                        onChange={changeBusinessSector}
                                        filterOption={(input, option): any =>
                                            String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                        }>
                                    {businessSectors.map((item: any) =>
                                        <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('certificate.template')} name={'template'}
                                       rules={[{required: true}]}>
                                <Select allowClear showSearch style={{width: '100%'}}
                                        placeholder={t('general.notSelected')}
                                        onChange={changeTemplate}
                                        disabled={!businessSectorId}
                                        filterOption={(input, option): any =>
                                            String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                        }>
                                    {filteredTemplates.map((item: any) =>
                                        <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>
                                    )}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        {templateParameters.map(parameter => <Col key={parameter.id} span={24} md={12} xl={8}>
                            <Form.Item label={parameter.name} name={'certificateParameterValues.' + parameter.id}
                                       rules={[{max: 2000, required: parameter.required, type: (parameter.type !== TEMPLATE_PARAM_DATE ? 'string' : 'object') }]}>
                                {
                                    parameter.type !== TEMPLATE_PARAM_DATE 
                                    ? <Input onBlur={event => {
                                        const values = {...form.getFieldsValue()};
                                        values['certificateParameterValues.' + parameter.id] = String(event.target.value).trim();
                                        form.setFieldsValue(values);
                                    }}/>
                                    : <DatePicker 
                                        placeholder=""
                                        className="ant-input"
                                        format={dateFormat}
                                        onChange={date => {
                                            const values = {...form.getFieldsValue()};
                                            values['certificateParameterValues.' + parameter.id] = date;
                                            form.setFieldsValue(values);
                                        }}
                                    />
                                }

                                
                            </Form.Item>
                        </Col>)}
                    </Row>
                </div>
                <div className={'subpage-content'}>
                    <Row className={'row-margin-bottom'}>
                        <Col span={24}>
                            <h2>{t('certificate.links')}</h2>
                        </Col>
                        <Col span={24} className={'mb-05'}>
                            <CertificatesListSearch
                                certificates={links}
                                
                                onRemove={(certificate: CertificateListItemDto) => {
                                    setLinks(links.filter(link => link.id !== certificate.id));
                                }}
                            />
                        </Col>
                        <Col span={24} className={'flex-row'}>
                            <div className={'flex-auto'}>
                                <Button className={'ant-btn-secondary button-with-icon white-background'}
                                        onClick={() => setSearchOpen(true)} disabled={!templateId}>
                                    <Search/> {t('certificate.searchLink')}
                                </Button>
                            </div>
                            <div>
                                <Button className={'ant-btn-secondary button-with-icon white-background'}
                                        onClick={() => setScanOpen(true)} disabled={!templateId}>
                                    <Maximize/> {t('certificate.scanLink')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                    <Row className={'row-margin-bottom'}>
                        <Col span={24}>
                            <h2>{t('certificate.files')}</h2>
                        </Col>
                        <Col span={24}>
                            <Upload
                                action={REACT_APP_API_DOMAIN + '/api/Certificate/file/upload'}
                                headers={getCustomHeaders() as any}
                                listType="picture"
                                fileList={[...fileList]}
                                itemRender={(originNode, file, currFileList) => (
                                    <div className="ant-upload-list-item ant-upload-list-item-done ant-upload-list-item-list-type-picture">
                                        <div className="ant-upload-list-item-info">
                                            <span className="ant-upload-span">
                                                
                                                <span className="ant-upload-list ant-upload-list-item-card-actions picture">
                                                    <button onClick={ () => handleRemoveFile(file) } title="Remove file" type="button" className="ant-btn ant-btn-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-card-actions-btn">
                                                        <XCircle />
                                                    </button>
                                                </span>
                                                <span className="ant-upload-list-item-name pt-05" title={file.name}>{file.name}</span>
                                            </span>
                                        </div>
                                    </div>
                                  )}
                                beforeUpload={beforeUpload}
                                disabled={!templateId}
                                onChange={event => {
                                    if (event.file.error) {
                                        showError(t('general.error'), t('error.fileUpload'));
                                    }
                                    setUploading(event.file.percent !== 100);
                                    setFileList(event.fileList.filter(item => !item.error && !!item.status));
                                }}
                            >
                                <Button className={'ant-btn-secondary button-with-icon white-background'}>
                                    <UploadIcon/> {t('general.upload')}
                                </Button>
                               
                            </Upload>
                        </Col>
                    </Row>
                    <Form.Item className={'no-margin text-right'}>
                        <Button className={'accent2-background button-with-icon text-white w-100'} type="primary"
                                htmlType="submit" disabled={uploading || !templateId}>
                            <Link/> {t('certificate.save')}
                        </Button>
                    </Form.Item>
                </div>
            </Form>
            <SearchCertificates
                header={t('certificate.searchLinkTitle')}
                show={searchOpen}
                multiSelect={true}
                addLinksButton={true}
                onClose={() => setSearchOpen(false)}
                onClick={(certificates: CertificateListItemDto[]) => {
                    const duplicatesCheck = certificates.filter(x => !links.some(l => l.id === x.id));
                    setLinks([...links, ...duplicatesCheck]);
                }}
            />
            <ScanQrCode
                header={t('certificate.scanLinkTitle')}
                show={scanOpen}
                onClose={() => setScanOpen(false)}
                onSuccess={(certificate: CertificateDto) => {
                    if (!links.find(link => link.id === certificate.id)) {
                        setLinks([...links, {
                            id: certificate.id,
                            hash: certificate.blockChainHashes?.certificateHash,
                            created: certificate.created,
                            documentCount: (certificate.files || []).length
                        } as any]);
                    }
                }}
            />
            <CertificateAnimation show={savingAnimation}
                                  withFiles={!!fileList.length}
                                  loading={!disableAnimation && certificateCreateResponse.loading}
                                  callback={() => {
                                      if (responseId) {
                                          history.push(`/certificates/${responseId}?businessSector=${businessSectorId}`);
                                      }
                                  }}/>
        </Spin>
    )
}
